import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button, Tooltip } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  HourglassOutlined,
  CloseOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { BiSolidBadge } from "react-icons/bi";
import { LuFileClock } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { useRecurrenceData } from "../../hooks/useRecurrenceData";
import { RecurrenceTimelineSkeleton } from "../skeletons/RecurrenceTimelineSkeleton";
import useObligationDetailsStore from "../../stores/useObligationDetailsStore";
import useAuthStore from "stores/useAuthStore";
import { getStatusColorByDisplayLabel } from "utils/statusConfig";
import dayjs from "utils/dayjsConfig";
import useScrollArrows from "hooks/useScrollArrows";
import InteractiveDiv from "components/universal/InteractiveDiv/InteractiveDiv";
import "../../css/widgets/ODRecurrenceTimeline.css";
import { resolveId } from "../../utils/ODGeneralUtils";

const SELECTION_PRIORITY_ORDER = [
  { statuses: ["late"], oldest: true },
  { statuses: ["working on"], oldest: true },
  { statuses: ["completed", "completed late"], oldest: false },
  { statuses: ["scheduled"], oldest: true },
];

const STATUS_NODE_CONFIG = {
  "completed": { icon: <CheckCircleOutlined /> },
  "completed late": { icon: <CheckCircleOutlined /> },
  "late": { icon: <ExclamationCircleOutlined /> },
  "working on": { icon: <ClockCircleOutlined /> },
  "scheduled": { icon: <CalendarOutlined /> },
};

const MOCK_BADGE_OVERRIDES = ["evidence accepted", "evidence rejected", "pending verification"];

export const VERIFICATION_BADGE_CONFIG = {
  "pending completion": {
    icon: <ClockCircleOutlined />,
    labelKey: "common.verification_status.pending_completion",
    countKey: "pendingCompletionCount",
    bg: "#E6E6E6",
    font: "#999999",
  },
  "pending verification": {
    icon: <HourglassOutlined />,
    labelKey: "common.verification_status.pending_approval",
    countKey: "pendingVerificationCount",
    bg: "#dbeafe",
    font: "#1677ff",
  },
  "evidence rejected": {
    icon: <CloseOutlined />,
    labelKey: "common.verification_status.evidence_rejected",
    countKey: "evidenceRejectedCount",
    bg: "#E1C4C6",
    font: "#852A2E",
  },
  "evidence accepted": {
    icon: <CheckOutlined />,
    labelKey: "common.verification_status.evidence_accepted",
    countKey: "evidenceAcceptedCount",
    bg: "#306df0",
    font: "#ffffff",
  },
  "pending criteria verification": {
    icon: <LuFileClock />,
    labelKey: "common.verification_status.pending_criteria_verification",
    countKey: "pendingCriteriaVerificationCount",
    bg: "#BCCEE5",
    font: "#093E7D",
  },
};

const findPriorityIndex = (events) => {
  for (const { statuses, oldest } of SELECTION_PRIORITY_ORDER) {
    let found = -1;
    let foundDate = null;

    events.forEach((item, i) => {
      const status = item?.status?.toLowerCase();
      if (!statuses.includes(status)) return;
      const date = item?.due_date ? new Date(item.due_date) : null;
      if (!date || isNaN(date)) return;
      if (foundDate === null || (oldest ? date < foundDate : date > foundDate)) {
        foundDate = date;
        found = i;
      }
    });

    if (found !== -1) return found;
  }
  return 0;
};

/**
 * When the current user is a verification assignee in the verification variant,
 * try to auto-select the first recurrence with "pending verification" aggregate
 * status that they are responsible for verifying.
 * Returns the index of the first matching event, or -1 if none found.
 */
const findVerificationPriorityIndex = (events, currentUserId) => {
  if (!currentUserId) return -1;

  for (let i = 0; i < events.length; i++) {
    const item = events[i];
    const aggStatus = (item?.aggregate_verification_status ?? "").toLowerCase().trim();
    if (aggStatus !== "pending verification") continue;

    const responsible = Array.isArray(item?.verification_responsible)
      ? item?.verification_responsible?.[0]
      : item?.verification_responsible;
    if (
      responsible?.user_id === currentUserId ||
      responsible?._id === currentUserId ||
      responsible?.id === currentUserId
    ) {
      return i;
    }
  }
  return -1;
};

const ODRecurrenceTimeline = ({ useMock = false }) => {
  const { t } = useTranslation("obligationDetails");
  const { fetchRecurrenceData } = useRecurrenceData();

  const selectedObligation = useObligationDetailsStore((s) => s.selectedObligation);
  const isLoadingObligation = useObligationDetailsStore((s) => s.isLoadingObligation);
  const selectedRecurrence = useObligationDetailsStore((s) => s.selectedRecurrence);
  const selectedFile = useObligationDetailsStore((s) => s.selectedFile);
  const activeVariant = useObligationDetailsStore((s) => s.activeVariant);
  const setSelectedRecurrence = useObligationDetailsStore((s) => s.setSelectedRecurrence);
  const setIsSelectingRecurrence = useObligationDetailsStore((s) => s.setIsSelectingRecurrence);
  const setLoadingRecurrence = useObligationDetailsStore((s) => s.setLoadingRecurrence);
  const isCompact = useObligationDetailsStore((s) => s.compact?.header);
  const interactionResetKey = useObligationDetailsStore((s) => s.interactionResetKey ?? 0);
  const isUserVerificationAssignee = useObligationDetailsStore((s) => s.isUserVerificationAssignee);
  const isVerificationRequiredForGroup = useObligationDetailsStore((s) => s.isVerificationRequiredForGroup);
  const userData = useAuthStore((s) => s.userData);

  const rawEvents = selectedObligation?.obligations ?? [];
  const recurrenceId = resolveId(selectedRecurrence, "obligation_id", "_id", "id");

  // ─── Verification filter ──────────────────────────────────────────────────
  // When activeVariant is "verification", exclude events with no aggregate verification status
  // Nodes not flagged to be verified always have null as their status and they shouldn't be displayed
  const recurrenceEvents = useMemo(() => {
    if (activeVariant !== "verification") return rawEvents;
    return rawEvents.filter((e) => e?.needs_verification === true);
  }, [rawEvents, activeVariant]);

  const nodeSize = isCompact
    ? { selected: 44, unselected: 36, fontSel: 18, font: 16, borderSel: 2, border: 2, dateFontSel: 13, dateFont: 12, datePadSel: "3px 10px", datePad: "3px 6px" }
    : { selected: 56, unselected: 48, fontSel: 24, font: 20, borderSel: 3, border: 2, dateFontSel: 14, dateFont: 13, datePadSel: "4px 12px", datePad: "4px 8px" };

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lineWidth, setLineWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const { scrollViewportRef, canGoLeft, canGoRight, getScrollHandlers } = useScrollArrows([recurrenceEvents]);
  const contentRef = useRef(null);

  // Stable string key for the current filtered event list.
  // Changes when the event list or the active variant filter changes.
  const eventsSig = useMemo(
    () => recurrenceEvents.map((e) => e._id).join(","),
    [recurrenceEvents],
  );

  const pendingIdRef = useRef(null);
  const queueRunningRef = useRef(false);
  const [selectionBusy, setSelectionBusy] = useState(false);

  // ─── Sync loading state ───────────────────────────────────────────────────
  useEffect(() => {
    setLoadingRecurrence(selectionBusy);
  }, [selectionBusy, setLoadingRecurrence]);

  // ─── Measure content + line width dynamically ─────────────────────────────
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const observe = () => {
      const total = content.scrollWidth;
      const style = window.getComputedStyle(content);
      const pl = parseFloat(style.paddingLeft) || 0;
      const pr = parseFloat(style.paddingRight) || 0;
      setContentWidth(total);
      setLineWidth(total - pl - pr);
    };

    const handle = requestAnimationFrame(observe);
    const ro = new ResizeObserver(observe);
    ro.observe(content);
    return () => {
      cancelAnimationFrame(handle);
      ro.disconnect();
    };
  }, [recurrenceEvents, isCompact, interactionResetKey]);

  // ─── Drain selection queue ────────────────────────────────────────────────
  const drainSelectionQueue = useCallback(async () => {
    if (queueRunningRef.current) return;
    queueRunningRef.current = true;
    setSelectionBusy(true);
    setIsSelectingRecurrence(true);
    try {
      while (true) {
        const targetId = pendingIdRef.current;
        if (!targetId) break;
        const details = await fetchRecurrenceData(targetId);
        if (pendingIdRef.current !== targetId) continue;
        if (details) setSelectedRecurrence(details);
        break;
      }
    } finally {
      queueRunningRef.current = false;
      setSelectionBusy(false);
      setIsSelectingRecurrence(false);
    }
  }, [fetchRecurrenceData, setSelectedRecurrence, setIsSelectingRecurrence]);

  // ─── Stale selection guard ────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedRecurrence) return;
    const stillExists = recurrenceEvents.some((e) => e._id === selectedRecurrence.obligation_id);
    if (!stillExists) {
      setSelectedRecurrence(null);
    }
  }, [recurrenceEvents, selectedRecurrence, setSelectedRecurrence]);

  // ─── Auto-select ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!recurrenceEvents.length) return;

    // Resolve recurrenceId to see if it's selectable
    const idIndex = recurrenceId
      ? recurrenceEvents.findIndex((e) => e._id === recurrenceId)
      : -1;

    let index;
    if (idIndex !== -1) {
      index = idIndex;
    } else if (isUserVerificationAssignee && activeVariant === "verification") {
      // Verification variant: prioritize first "pending verification" recurrence the user is assigned to verify
      const currentUserId = userData?._id || userData?.id || null;
      const vIndex = findVerificationPriorityIndex(recurrenceEvents, currentUserId);
      index = vIndex === -1 ? findPriorityIndex(recurrenceEvents) : vIndex;
    } else {
      index = findPriorityIndex(recurrenceEvents);
    }

    const id = recurrenceEvents[index]?._id;
    if (!id) return;

    setSelectedIndex(index);
    pendingIdRef.current = id;
    drainSelectionQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsSig, interactionResetKey, isUserVerificationAssignee, activeVariant, userData]);

  // ─── Scroll selected node into view ──────────────────────────────────────
  useEffect(() => {
    const node = scrollViewportRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    node?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedIndex]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleSelect = useCallback((index, id) => {
    setSelectedIndex(index);
    pendingIdRef.current = id;
    drainSelectionQueue();
  }, [drainSelectionQueue]);

  // ─── Background gradient ──────────────────────────────────────────────────
  const backgroundGradient = useMemo(() => {
    if (!recurrenceEvents.length) return "#cbd5e1";

    const maxDistance = Math.max(1, Math.floor(recurrenceEvents.length / 2));

    const hexToRgb = (hex) => {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r
        ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) }
        : { r: 148, g: 163, b: 184 };
    };

    const stops = recurrenceEvents.map((item, i) => {
      const distance = Math.abs(i - selectedIndex);
      const intensity = Math.max(0, 1 - Math.pow(distance / maxDistance, 2));
      const color = getStatusColorByDisplayLabel(item?.status?.toLowerCase(), "#94a3b8");
      const { r, g, b } = hexToRgb(color);
      const alpha = 0.12 + intensity * 0.48;
      const percent = recurrenceEvents.length === 1 ? 50 : (i / (recurrenceEvents.length - 1)) * 100;
      return `rgba(${r}, ${g}, ${b}, ${alpha}) ${percent}%`;
    });

    return `linear-gradient(90deg, ${stops.join(", ")})`;
  }, [recurrenceEvents, selectedIndex]);

  // ─── Guards ───────────────────────────────────────────────────────────────
  if (isLoadingObligation) {
    return (
      <div className="odrt-container">
        <RecurrenceTimelineSkeleton />
      </div>
    );
  }

  if (!recurrenceEvents.length) return null;

  const showArrows = canGoLeft || canGoRight;

  return (
    <div className="odrt-container" data-compact={isCompact || undefined}>
      {showArrows && (
        <>
          <Button
            className="odrt-nav-arrow odrt-nav-arrow--left"
            type="text"
            icon={<LeftOutlined />}
            disabled={!canGoLeft}
            {...getScrollHandlers({ x: -1 }, { clickDistancePx: selectedFile ? 260 : 300 })}
          />

          <Button
            className="odrt-nav-arrow odrt-nav-arrow--right"
            type="text"
            icon={<RightOutlined />}
            disabled={!canGoRight}
            {...getScrollHandlers({ x: 1 }, { clickDistancePx: selectedFile ? 260 : 300 })}
          />
        </>
      )}

      <div className="odrt-scroll-viewport" ref={scrollViewportRef}>
        <div
          className="odrt-scroll-canvas"
          ref={contentRef}
          style={{ width: contentWidth || undefined }}
        >
          <div
            className="odrt-background-line"
            style={{ background: backgroundGradient, width: lineWidth || undefined }}
          />

          {recurrenceEvents.map((item, index) => {
            const status = item?.status?.toLowerCase();
            const statusColor = getStatusColorByDisplayLabel(status);
            const statusNodeConfig = STATUS_NODE_CONFIG[status];
            // First 3 nodes can use hardcoded mock statuses for showcase purposes when useMock is true
            const verificationStatus = (useMock && index < MOCK_BADGE_OVERRIDES.length)
              ? MOCK_BADGE_OVERRIDES[index]
              : (item?.aggregate_verification_status?.toLowerCase() || null);
            const verificationBadgeConfig = (isVerificationRequiredForGroup && item?.needs_verification === true && verificationStatus)
              ? VERIFICATION_BADGE_CONFIG[verificationStatus]
              : null;

            const isSelected = selectedIndex === index;

            return (
              <InteractiveDiv
                key={item._id}
                className="odrt-event-item"
                data-index={index}
                onClick={() => handleSelect(index, item._id)}
              >
                <span className="odrt-event-responsible-label">
                  {item?.responsible || "--"}
                </span>

                <div className="odrt-event-node-wrapper">
                  <div
                    className="odrt-event-circle"
                    style={{
                      width: isSelected ? nodeSize.selected : nodeSize.unselected,
                      height: isSelected ? nodeSize.selected : nodeSize.unselected,
                      borderColor: statusColor,
                      color: statusColor,
                      borderWidth: isSelected ? nodeSize.borderSel : nodeSize.border,
                      boxShadow: isSelected ? `0 4px 16px ${statusColor}30` : `0 2px 8px ${statusColor}20`,
                      fontSize: isSelected ? nodeSize.fontSel : nodeSize.font,
                      animation: isSelected && status === "late" ? "pulse-animation 2s infinite" : "none",
                    }}
                  >
                    {statusNodeConfig?.icon || <CalendarOutlined />}
                  </div>

                  {verificationBadgeConfig && (
                    <Tooltip
                      title={(
                        <div className="odrt-event-badge-tooltip-title-wrapper">
                          <span>{t(verificationBadgeConfig.labelKey)}</span>
                          <span>
                            {t(
                              "widgets.recurrence_timeline.labels.approval_responsible",
                              { responsible: item?.verification_responsible?.[0]?.name || "--" }
                            )}
                          </span>
                        </div>
                      )}
                      placement="top"
                    >
                      <div
                        className={`odrt-event-badge${isSelected ? " odrt-event-badge--selected" : ""}`}
                        role="img"
                        aria-label={t(verificationBadgeConfig.labelKey)}
                      >
                        <BiSolidBadge
                          className="odrt-event-badge-shape"
                          style={{ color: verificationBadgeConfig.bg, filter: `drop-shadow(0 0 1px ${verificationBadgeConfig.font})` }}
                        />

                        <span
                          className={`odrt-event-badge-icon${isSelected ? " odrt-event-badge-icon--selected" : ""}`}
                          style={{ color: verificationBadgeConfig.font }}
                        >
                          {verificationBadgeConfig.icon}
                        </span>
                      </div>
                    </Tooltip>
                  )}
                </div>

                <div className="odrt-event-dates-wrapper">
                  <div className="odrt-event-date-group">
                    <h6 className="odrt-event-date-subheading">
                      {t("widgets.recurrence_timeline.labels.due_date")}
                    </h6>

                    <span
                      className="odrt-event-date-label"
                      style={{
                        padding: isSelected ? nodeSize.datePadSel : nodeSize.datePad,
                        fontSize: isSelected ? nodeSize.dateFontSel : nodeSize.dateFont,
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? "#ffffff" : "#64748b",
                        backgroundColor: isSelected ? statusColor : "transparent",
                        border: isSelected ? `1px solid ${statusColor}` : "1px solid transparent",
                      }}
                    >
                      {dayjs.utc(item?.due_date).format("DD/MMM/YYYY")}
                    </span>
                  </div>

                  {!isCompact && (
                    <div className="odrt-event-date-group">
                      <h6 className="odrt-event-date-subheading">
                        {t("widgets.recurrence_timeline.labels.completed_date")}
                      </h6>

                      <span
                        className="odrt-event-date-label"
                        style={{
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: isSelected ? nodeSize.dateFontSel : nodeSize.dateFont,
                        }}
                      >
                        {item?.completed_date
                          ? dayjs.utc(item.completed_date).format("DD/MMM/YYYY")
                          : "--"
                        }
                      </span>
                    </div>
                  )}
                </div>
              </InteractiveDiv>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ODRecurrenceTimeline);